<template>
  <div
    ref="rootRef"
    class="search-select"
    :class="[
      `search-select--${variant}`,
      `search-select--${size}`,
      { 'search-select--open': open, 'search-select--disabled': disabled },
    ]"
  >
    <button
      ref="triggerRef"
      type="button"
      class="search-select__trigger"
      :disabled="disabled"
      :aria-label="ariaLabel"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click="toggle"
      @keydown.down.prevent="openPanel"
      @keydown.enter.prevent="toggle"
    >
      <span
        class="search-select__value"
        :class="{ 'search-select__value--placeholder': !selectedLabel }"
      >
        {{ selectedLabel || placeholder }}
      </span>
      <svg class="search-select__chevron" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M6 8l4 4 4-4"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <Teleport to="body">
      <div v-if="open" ref="panelRef" class="search-select__panel" :style="panelStyle">
        <div class="search-select__search">
          <svg
            class="search-select__search-icon"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="9" cy="9" r="6" stroke="currentColor" stroke-width="1.5" />
            <path
              d="M13.5 13.5L18 18"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
          <input
            ref="searchRef"
            v-model="query"
            type="text"
            class="search-select__search-input"
            :placeholder="searchPlaceholder"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="selectHighlighted"
            @keydown.escape.prevent="close"
            @keydown.tab="close"
          />
        </div>
        <ul ref="listRef" class="search-select__list" role="listbox">
          <li v-if="filtered.length === 0" class="search-select__empty">{{ emptyText }}</li>
          <li
            v-for="(opt, i) in filtered"
            :key="String(opt.value)"
            role="option"
            :aria-selected="opt.value === modelValue"
            class="search-select__option"
            :class="{
              'search-select__option--active': i === activeIndex,
              'search-select__option--selected': opt.value === modelValue,
            }"
            @mousedown.prevent="selectOption(opt.value)"
            @mousemove="activeIndex = i"
          >
            {{ opt.label }}
          </li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'

type OptionValue = string | number

interface SelectOption {
  value: OptionValue
  label: string
}

const props = withDefaults(
  defineProps<{
    modelValue: OptionValue | null | undefined
    options: SelectOption[]
    placeholder?: string
    searchPlaceholder?: string
    emptyText?: string
    disabled?: boolean
    /** `light` for normal surfaces, `dark` for the (blue) sidebar. */
    variant?: 'light' | 'dark'
    /** `sm` matches compact table filters; `md` matches header/form fields. */
    size?: 'sm' | 'md'
    ariaLabel?: string
  }>(),
  {
    placeholder: 'Seleziona…',
    searchPlaceholder: 'Cerca…',
    emptyText: 'Nessun risultato',
    disabled: false,
    variant: 'light',
    size: 'sm',
    ariaLabel: undefined,
  },
)

const emit = defineEmits<{ 'update:modelValue': [value: OptionValue] }>()

const rootRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLButtonElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
const searchRef = ref<HTMLInputElement | null>(null)
const listRef = ref<HTMLElement | null>(null)

const open = ref(false)
const query = ref('')
const activeIndex = ref(0)
const panelStyle = ref<Record<string, string>>({})

const selectedLabel = computed(
  () => props.options.find((o) => o.value === props.modelValue)?.label ?? '',
)

const filtered = computed<SelectOption[]>(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return props.options
  return props.options.filter((o) => o.label.toLowerCase().includes(q))
})

/** Highlight the current selection when present, otherwise the first row. */
function resetActive() {
  const idx = filtered.value.findIndex((o) => o.value === props.modelValue)
  activeIndex.value = idx >= 0 ? idx : 0
}

watch(filtered, resetActive)

// Fixed-positioned, teleported panel so it is never clipped by an ancestor's
// overflow (the sidebar scrolls; the data tables scroll horizontally).
function reposition() {
  const el = triggerRef.value
  if (!el) return
  const r = el.getBoundingClientRect()
  const maxPanelH = 300
  const gap = 4
  const spaceBelow = window.innerHeight - r.bottom
  const openUp = spaceBelow < maxPanelH && r.top > spaceBelow
  const width = Math.max(Math.round(r.width), 200)
  let left = Math.round(r.left)
  if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8
  if (left < 8) left = 8
  panelStyle.value = {
    position: 'fixed',
    left: `${left}px`,
    width: `${width}px`,
    zIndex: '9999',
    ...(openUp
      ? { bottom: `${Math.round(window.innerHeight - r.top + gap)}px` }
      : { top: `${Math.round(r.bottom + gap)}px` }),
  }
}

function onViewportChange() {
  if (open.value) reposition()
}

function openPanel() {
  if (props.disabled || open.value) return
  query.value = ''
  // Position the panel BEFORE it renders so the teleported (initially static)
  // node never flashes at the bottom of the page and steals scroll.
  reposition()
  open.value = true
  resetActive()
  nextTick(() => {
    reposition()
    // preventScroll: focusing the field must never scroll the page — with a long
    // list behind it the page would otherwise jump to the bottom.
    searchRef.value?.focus({ preventScroll: true })
    scrollActiveIntoView()
  })
  // capture=true catches scrolls inside any ancestor, not just window.
  window.addEventListener('scroll', onViewportChange, true)
  window.addEventListener('resize', onViewportChange)
}

function close() {
  if (!open.value) return
  open.value = false
  window.removeEventListener('scroll', onViewportChange, true)
  window.removeEventListener('resize', onViewportChange)
}

function toggle() {
  if (open.value) close()
  else openPanel()
}

function selectOption(value: OptionValue) {
  emit('update:modelValue', value)
  close()
}

function selectHighlighted() {
  const opt = filtered.value[activeIndex.value]
  if (opt) selectOption(opt.value)
}

function move(delta: number) {
  const n = filtered.value.length
  if (!n) return
  activeIndex.value = (activeIndex.value + delta + n) % n
  scrollActiveIntoView()
}

function scrollActiveIntoView() {
  nextTick(() => {
    const list = listRef.value
    const active = list?.querySelector<HTMLElement>('.search-select__option--active')
    if (!list || !active) return
    // Scroll only WITHIN the options list — never scrollIntoView(), which can
    // scroll the whole page (the panel is teleported / position: fixed).
    const lr = list.getBoundingClientRect()
    const ar = active.getBoundingClientRect()
    if (ar.top < lr.top) list.scrollTop -= lr.top - ar.top
    else if (ar.bottom > lr.bottom) list.scrollTop += ar.bottom - lr.bottom
  })
}

function onDocPointer(e: PointerEvent) {
  const t = e.target as Node
  if (rootRef.value?.contains(t)) return
  if (panelRef.value?.contains(t)) return
  close()
}

watch(open, (v) => {
  if (v) document.addEventListener('pointerdown', onDocPointer, true)
  else document.removeEventListener('pointerdown', onDocPointer, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocPointer, true)
  window.removeEventListener('scroll', onViewportChange, true)
  window.removeEventListener('resize', onViewportChange)
})
</script>

<style scoped lang="scss">
.search-select {
  position: relative;
  width: 100%;
  box-sizing: border-box;
}

.search-select__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  border: 1px solid var(--cds-border-subtle, #e0e0e0);
  background-color: var(--cds-layer-02, #f4f4f4);
  color: var(--cds-text-primary, #161616);
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  &:focus-visible {
    outline: 2px solid var(--cds-focus, #0f62fe);
    outline-offset: -1px;
  }
}

.search-select--disabled .search-select__trigger {
  opacity: 0.5;
  cursor: not-allowed;
}

// Sizes
.search-select--sm .search-select__trigger {
  padding: 4px 8px;
  font-size: var(--text-size-xs, 0.75rem);
  border-radius: 4px;
}

.search-select--md .search-select__trigger {
  padding: 6px 12px;
  font-size: var(--text-size-sm, 0.875rem);
  border-radius: 8px;
}

.search-select__value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-select__value--placeholder {
  color: var(--cds-text-placeholder, #a8a8a8);
}

.search-select__chevron {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  opacity: 0.6;
  transition: transform 0.15s ease;
}

.search-select--open .search-select__chevron {
  transform: rotate(180deg);
}

// Dark variant — only the trigger changes; the popover stays light for legibility.
.search-select--dark .search-select__trigger {
  border-color: rgba(255, 255, 255, 0.3);
  background-color: rgba(255, 255, 255, 0.1);
  color: inherit;
}

.search-select--dark .search-select__value--placeholder {
  color: rgba(255, 255, 255, 0.6);
}

// Teleported popover (rendered at <body>; scoped styles still apply via data-v).
.search-select__panel {
  display: flex;
  flex-direction: column;
  max-height: 300px;
  background-color: var(--cds-layer-01, #fff);
  border: 1px solid var(--cds-border-subtle, #e0e0e0);
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.search-select__search {
  position: relative;
  display: flex;
  align-items: center;
  padding: 6px;
  border-bottom: 1px solid var(--cds-border-subtle, #e0e0e0);
}

.search-select__search-icon {
  position: absolute;
  left: 14px;
  width: 14px;
  height: 14px;
  color: var(--cds-text-secondary, #525252);
  pointer-events: none;
}

.search-select__search-input {
  width: 100%;
  border: none;
  outline: none;
  padding: 6px 8px 6px 28px;
  font-family: inherit;
  font-size: var(--text-size-sm, 0.875rem);
  color: var(--cds-text-primary, #161616);
  background: transparent;

  &::placeholder {
    color: var(--cds-text-placeholder, #a8a8a8);
  }
}

.search-select__list {
  list-style: none;
  margin: 0;
  padding: 4px;
  overflow-y: auto;
}

.search-select__option {
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: var(--text-size-sm, 0.875rem);
  color: var(--cds-text-primary, #161616);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-select__option--active {
  background-color: var(--cds-layer-hover, #e8e8e8);
}

.search-select__option--selected {
  background-color: rgba(15, 98, 254, 0.1);
  color: #0f62fe;
  font-weight: var(--font-weight-medium, 500);
}

.search-select__empty {
  padding: 10px;
  font-size: var(--text-size-sm, 0.875rem);
  color: var(--cds-text-secondary, #525252);
  text-align: center;
}
</style>
